(() => {
  'use strict';
  const body = document.body;
  if (!body || !body.classList.contains('ssp-firstlights')) return;
  if (window.SSPFirstLightsHeader) { window.SSPFirstLightsHeader.init(document); return; }

  const selector = '[data-ssp-header-lights]';
  const toggleSelector = '[data-ssp-header-light-toggle]';
  const storageKey = 'ssp-firstlights-header-off';
  const motionStorageKey = 'ssp-firstlights-header-paused';
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const strands = new Map();
  let manualOff = false;
  let lightsPaused = false;
  let startupHandled = false;
  try {
    manualOff = window.sessionStorage.getItem(storageKey) === '1';
    lightsPaused = window.sessionStorage.getItem(motionStorageKey) === '1';
  } catch (_) { /* Storage may be blocked. */ }

  const globalPaused = () => body.dataset.seasonMotion === 'paused';
  const motionPaused = () => lightsPaused || preference.matches || globalPaused();
  const updateMotionControls = () => {
    const paused = motionPaused();
    document.querySelectorAll('[data-header-light-motion-toggle]').forEach(button => {
      const labels = button.dataset;
      const unavailable = preference.matches || globalPaused();
      const label = unavailable ? labels.headerLightsPausedLabel
        : paused ? labels.headerLightsPlayLabel : labels.headerLightsPauseLabel;
      const aria = preference.matches ? labels.headerLightsReducedAria
        : globalPaused() ? labels.headerLightsGlobalAria
        : paused ? labels.headerLightsPlayAria : labels.headerLightsPauseAria;
      if (label) button.textContent = label;
      if (aria) button.setAttribute('aria-label', aria);
      button.setAttribute('aria-pressed', String(paused));
      button.disabled = unavailable;
      button.title = unavailable ? aria || '' : '';
    });
  };
  const savePreference = () => {
    try {
      if (manualOff) window.sessionStorage.setItem(storageKey, '1');
      else window.sessionStorage.removeItem(storageKey);
      if (lightsPaused) window.sessionStorage.setItem(motionStorageKey, '1');
      else window.sessionStorage.removeItem(motionStorageKey);
    } catch (_) { /* Keep the in-memory preference for this page. */ }
  };

  function createStrand(strand) {
    const buttons = [...strand.querySelectorAll(toggleSelector)];
    let entryObserver = null;
    let firstFrame = 0;
    let startFrame = 0;
    let settleTimer = 0;
    let disposed = false;

    const cancelStartup = () => {
      entryObserver?.disconnect();
      entryObserver = null;
      if (firstFrame) window.cancelAnimationFrame(firstFrame);
      if (startFrame) window.cancelAnimationFrame(startFrame);
      if (settleTimer) window.clearTimeout(settleTimer);
      firstFrame = startFrame = settleTimer = 0;
    };
    const settle = () => {
      cancelStartup();
      strand.dataset.lightState = manualOff ? 'off' : 'steady';
    };
    const updateButtons = () => buttons.forEach(button => {
      button.disabled = false;
      button.setAttribute('aria-pressed', String(!manualOff));
      button.title = manualOff ? strand.dataset.lightOnLabel : strand.dataset.lightOffLabel;
    });
    const setTabStop = button => buttons.forEach(item => { item.tabIndex = item === button ? 0 : -1; });
    const resetTabStop = () => {
      const visible = buttons.filter(button => button.getClientRects().length > 0);
      const current = visible.find(button => button === document.activeElement)
        || visible.find(button => button.tabIndex === 0) || visible[0];
      setTabStop(current);
    };
    const beginWarmup = () => {
      if (disposed) return;
      entryObserver?.disconnect();
      entryObserver = null;
      if (startupHandled || manualOff || motionPaused()) { startupHandled = true; settle(); return; }
      if (document.hidden) return;
      // Claim the page's single startup before queuing paint frames. Section
      // reloads, bfcache returns and ordinary scrolling cannot replay it.
      startupHandled = true;
      firstFrame = window.requestAnimationFrame(() => {
        firstFrame = 0;
        startFrame = window.requestAnimationFrame(() => {
          startFrame = 0;
          if (disposed || manualOff || motionPaused() || document.hidden) { settle(); return; }
          strand.dataset.lightState = 'warming';
          // Longer than 2200ms plus the largest 372ms bulb offset; also cleans
          // up if animationend is unavailable or CSS is suppressed.
          settleTimer = window.setTimeout(settle, 2700);
        });
      });
    };
    const sync = () => {
      if (disposed) return;
      updateButtons();
      strand.dataset.lightMotion = document.hidden || motionPaused() ? 'paused' : 'playing';
      if (manualOff || motionPaused()) { startupHandled = true; settle(); return; }
      if (document.hidden) {
        if (strand.dataset.lightState === 'warming' || firstFrame || startFrame) settle();
        entryObserver?.disconnect();
        entryObserver = null;
        return;
      }
      if (strand.dataset.lightState === 'warming' || firstFrame || startFrame) return;
      if (startupHandled) { settle(); return; }
      if (entryObserver) return;
      if (!('IntersectionObserver' in window)) {
        const rect = strand.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        if (rect.bottom > 0 && rect.top < viewportHeight) beginWarmup();
        else { startupHandled = true; settle(); }
        return;
      }
      entryObserver = new IntersectionObserver(entries => {
        if (disposed) return;
        const entry = entries.filter(item => item.target === strand).at(-1);
        if (!entry) return;
        // This is a page-open visibility gate, not a scroll trigger. An
        // initially offscreen/restored header stays steady when revisited.
        entryObserver?.disconnect();
        entryObserver = null;
        if (document.hidden) return;
        if (entry.isIntersecting && entry.intersectionRatio > 0) beginWarmup();
        else { startupHandled = true; settle(); }
      }, { threshold: 0 });
      entryObserver.observe(strand);
    };
    const onClick = event => {
      const button = event.target.closest?.(toggleSelector);
      if (!button || !strand.contains(button)) return;
      manualOff = !manualOff;
      startupHandled = true;
      savePreference();
      setTabStop(button);
      strands.forEach(controller => controller.sync());
    };
    const onKeyDown = event => {
      const button = event.target.closest?.(toggleSelector);
      if (!button || !strand.contains(button)) return;
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      const visible = buttons.filter(item => item.getClientRects().length > 0);
      const index = visible.indexOf(button);
      if (index < 0) return;
      event.preventDefault();
      let next = event.key === 'Home' ? 0 : event.key === 'End' ? visible.length - 1
        : (index + (event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1) + visible.length) % visible.length;
      setTabStop(visible[next]);
      visible[next].focus();
    };
    strand.addEventListener('click', onClick);
    strand.addEventListener('keydown', onKeyDown);
    strand.dataset.lightState = manualOff ? 'off' : 'steady';
    strand.dataset.lightReady = 'page-open-v1';
    resetTabStop();
    updateButtons();
    return {
      sync,
      resetTabStop,
      suspend() {
        startupHandled = true;
        strand.dataset.lightMotion = 'paused';
        settle();
      },
      destroy() {
        if (disposed) return;
        disposed = true;
        cancelStartup();
        strand.removeEventListener('click', onClick);
        strand.removeEventListener('keydown', onKeyDown);
        buttons.forEach(button => { button.disabled = true; button.tabIndex = -1; });
        strand.dataset.lightState = manualOff ? 'off' : 'steady';
        delete strand.dataset.lightReady;
        delete strand.dataset.lightMotion;
      }
    };
  }

  const syncAll = () => {
    updateMotionControls();
    strands.forEach(controller => controller.sync());
  };
  const init = (root = document) => {
    strands.forEach((controller, strand) => {
      if (!strand.isConnected) { controller.destroy(); strands.delete(strand); }
    });
    if (!body.classList.contains('ssp-firstlights')) return;
    const candidates = [...(root.querySelectorAll?.(selector) || [])];
    if (root.matches?.(selector)) candidates.unshift(root);
    candidates.forEach(strand => {
      if (!strands.has(strand)) strands.set(strand, createStrand(strand));
    });
    syncAll();
  };
  window.SSPFirstLightsHeader = { init };
  const pauseObserver = new MutationObserver(syncAll);
  pauseObserver.observe(body, { attributes: true, attributeFilter: ['data-season-motion'] });
  preference.addEventListener('change', syncAll);
  document.addEventListener('visibilitychange', syncAll);
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-header-light-motion-toggle]');
    if (!button || !body.contains(button) || preference.matches || globalPaused()) return;
    event.preventDefault();
    lightsPaused = !lightsPaused;
    savePreference();
    syncAll();
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    strands.forEach((controller, strand) => {
      if (event.target === strand || event.target.contains?.(strand)) {
        controller.destroy();
        strands.delete(strand);
      }
    });
  });
  window.addEventListener('resize', () => strands.forEach(controller => controller.resetTabStop()), { passive: true });
  window.addEventListener('pagehide', () => strands.forEach(controller => controller.suspend()));
  window.addEventListener('pageshow', () => init(document));
  init(document);
})();
